export interface TreatmentPlanQuickPromptOption {
  id: string;
  label: string;
  prompt: string;
}

/** Registry order is the composition order for multi-select prompts. */
export const TREATMENT_PLAN_REVISION_QUICK_PROMPTS: readonly TreatmentPlanQuickPromptOption[] = [
  {
    id: 'measurable-goals',
    label: 'Make goals measurable',
    prompt: 'Make goals measurable with specific frequency, duration, and observable criteria.',
  },
  {
    id: 'clinical-language',
    label: 'Tighten clinical language',
    prompt: 'Tighten clinical language for clarity and professional tone without changing facts.',
  },
  {
    id: 'improve-objectives',
    label: 'Improve objectives',
    prompt: 'Improve objectives so they are specific, measurable, and time-bound.',
  },
  {
    id: 'simplify-wording',
    label: 'Simplify wording',
    prompt: 'Simplify wording for client readability while preserving clinical meaning.',
  },
  {
    id: 'medical-necessity',
    label: 'Strengthen medical necessity',
    prompt: 'Strengthen medical necessity language using only documented facts.',
  },
] as const;

/**
 * Compose outgoing instruction from selected option ids (registry order) plus free text.
 * Does not overwrite free text; avoids duplicate prompt fragments.
 */
export function composeTreatmentPlanRevisionInstruction(
  selectedIds: readonly string[],
  freeText: string,
  options: readonly TreatmentPlanQuickPromptOption[] = TREATMENT_PLAN_REVISION_QUICK_PROMPTS,
): string {
  const selectedPrompts: string[] = [];
  const seen = new Set<string>();
  for (const option of options) {
    if (!selectedIds.includes(option.id)) continue;
    const text = option.prompt.trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    selectedPrompts.push(text);
  }

  const additional = freeText.trim();
  if (selectedPrompts.length === 0) return additional;
  if (!additional) {
    return `Requested changes:\n${selectedPrompts.map((p) => `- ${p}`).join('\n')}`;
  }
  return [
    'Requested changes:',
    ...selectedPrompts.map((p) => `- ${p}`),
    '',
    'Additional instruction:',
    additional,
  ].join('\n');
}
